import { useEffect, useRef, useState } from "react";

/**
 * S9b T13 — Cloudflare Turnstile widget for landing forms.
 * (Mirror of apps/web/src/components/TurnstileWidget.tsx — kept separate so
 * landing has no dependency on web's component graph.)
 */

type Props = {
  onToken: (token: string) => void;
  theme?: "light" | "dark" | "auto";
  testBypass?: boolean;
  /**
   * Bump this to discard a spent token and issue a fresh challenge.
   *
   * A Turnstile token can be redeemed ONCE. When the API rejects one, the
   * widget is still sitting on that same spent token, so simply clearing the
   * parent's state leaves the form with no way to ever produce a valid one —
   * every retry fails. reset() re-renders in place and fires `callback` again
   * with a new token. Starts at 0, which means "never reset".
   *
   * apps/web's widget already does this behind its Retry button; this is the
   * landing mirror catching up.
   */
  resetSignal?: number;
  /**
   * Test seam. `import.meta.env` cannot be stubbed mid-test in jsdom (see
   * apps/web/src/components/TurnstileWidget.test.tsx), so without this the
   * widget always takes the "no site key" branch under test and the reset
   * path could never be exercised. Same shape as web's testForceError.
   */
  testSiteKey?: string | undefined;
};

declare global {
  interface Window {
    turnstile?: {
      render: (
        element: HTMLElement,
        opts: {
          sitekey: string;
          action?: string;
          theme?: string;
          callback?: (token: string) => void;
          "error-callback"?: () => void;
          "expired-callback"?: () => void;
        },
      ) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js";

function getSiteKey(): string {
  return (
    (import.meta as unknown as { env?: Record<string, string> }).env
      ?.VITE_TURNSTILE_SITE_KEY ?? ""
  );
}

export function TurnstileWidget({
  onToken,
  theme = "light",
  testBypass,
  resetSignal = 0,
  testSiteKey,
}: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const siteKey = testSiteKey ?? getSiteKey();

  useEffect(() => {
    if (testBypass) {
      onToken("test-token");
      return;
    }
    if (!siteKey) {
      onToken("");
      return;
    }
    const existing = document.querySelector(`script[src="${SCRIPT_SRC}"]`);
    if (!existing) {
      const s = document.createElement("script");
      s.src = SCRIPT_SRC;
      s.async = true;
      s.defer = true;
      document.head.appendChild(s);
    }

    let cancelled = false;
    const tryRender = () => {
      if (cancelled) return;
      if (!window.turnstile || !ref.current) {
        setTimeout(tryRender, 200);
        return;
      }
      try {
        widgetIdRef.current = window.turnstile.render(ref.current, {
          sitekey: siteKey,
          action: "turnstile-spin-v1",
          theme,
          callback: (token: string) => onToken(token),
          "error-callback": () => setError("Verification failed. Please retry."),
          "expired-callback": () => onToken(""),
        });
      } catch (e) {
        setError((e as Error).message);
      }
    };
    tryRender();
    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          // ignore
        }
      }
    };
  }, [onToken, theme, siteKey, testBypass]);

  // Reset on demand. Deliberately a SEPARATE effect: folding resetSignal into
  // the render effect above would tear the widget down and rebuild it, losing
  // the Cloudflare host frame. reset() is an in-place rerender.
  useEffect(() => {
    if (resetSignal === 0) return;
    if (testBypass) {
      onToken("test-token");
      return;
    }
    const id = widgetIdRef.current;
    if (!id || !window.turnstile || typeof window.turnstile.reset !== "function") {
      return;
    }
    try {
      setError(null);
      // Drop the spent token BEFORE reset(), not after. Cloudflare normally
      // delivers the replacement asynchronously, but it is not contractually
      // required to — and a host that fires `callback` synchronously from
      // reset() would have the clear land on top of the FRESH token, leaving
      // the form with nothing to submit. Clearing first is correct either way.
      onToken("");
      window.turnstile.reset(id);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [resetSignal, onToken, testBypass]);

  if (testBypass) return null;
  if (!siteKey) {
    return (
      <div className="text-xs text-ink-500" data-testid="turnstile-disabled">
        (CAPTCHA not configured — dev mode)
      </div>
    );
  }

  return (
    <div>
      <div ref={ref} data-testid="turnstile-widget" />
      {error && (
        <p role="alert" className="text-xs text-red-600 mt-1">
          {error}
        </p>
      )}
    </div>
  );
}
